// SPDX-License-Identifier: GPL-3.0-or-later
// Larry, the reviewer SigmaBot: approves without reading (THEME.md section 5.6).
// A sleepy pear bot with a bowtie. He holds up a thumb. His clipboard shows the four lane letters L, G, T and M.

import { BoxGeometry } from "three";
import { BRAND_HEX } from "../art/canvas.ts";
import { drawClipboard } from "../art/labels.ts";
import { addBot } from "./bot.ts";
import {
  type Built,
  type CharacterEnv,
  disposeTree,
  makeLabel,
  motion,
  namedGroup,
  outlined,
  put,
  sec,
  solid,
} from "./common.ts";
import { SIGMABOT_NODES, type SigmaBotSpec } from "./sigmabot/index.ts";

/** The look of Larry. */
export const LARRY_SPEC: SigmaBotSpec = {
  body: "pear",
  palette: "mint",
  eyes: "sleepy",
  mouth: "grin",
  hat: "none",
  accessory: "bowtie",
};

/** Size of one body unit of Larry. */
const SCALE = 1;
/** The right arm goes up. This is the angle of the arm from straight down, in radians. */
const THUMB_ARM_ANGLE = 2.3;

/** Read the `letters:` arg: four characters of 0 or 1, in the order L, G, T, M. */
export function parseLetters(arg: string): boolean[] {
  return [0, 1, 2, 3].map((i) => arg[i] === "1");
}

export function buildLarry(env: CharacterEnv): Built {
  const group = namedGroup("Larry");
  const bot = addBot(group, "LarryBot", LARRY_SPEC, SCALE);

  // Thumbs-up: the right arm goes up and a white thumb sits on its hand.
  const arm = bot.getObjectByName(SIGMABOT_NODES.armR);
  if (arm) {
    arm.rotation.z = THUMB_ARM_ANGLE;
    arm.add(put(new BoxGeometry(8, 12, 6), solid(BRAND_HEX.edge), 0, -24, 0));
  }

  // Clipboard with the lane letters, held at his left side.
  let lit = [false, false, false, false];
  group.add(outlined(put(new BoxGeometry(26, 32, 2), solid(BRAND_HEX.gray222), -40, 34, 16)));
  const sheet = makeLabel(env, 22, 28, 128, 160, (ctx, w, h) => drawClipboard(ctx, w, h, lit));
  sheet.mesh.position.set(-40, 34, 17.2);
  group.add(sheet.mesh);

  return {
    group,
    tick(timeMs) {
      bot.rotation.x = 0.08 * Math.sin(sec(timeMs) * 1.0) * motion(env);
    },
    play(name, arg) {
      if (name !== "letters") return;
      const next = parseLetters(arg);
      if (next.some((v, i) => v !== lit[i])) {
        lit = next;
        sheet.redraw((ctx, w, h) => drawClipboard(ctx, w, h, lit));
      }
    },
    dispose() {
      disposeTree(group);
    },
  };
}
